import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import AnnotationRangeBand from './AnnotationRangeBand';
import RangeClusterPopover from './RangeClusterPopover';
import { getAnnotationColor } from '../utils/annotationPresentation';
import { getAnnotationTypeLabel } from '../utils/annotationDescriptionDisplay';
import { buildRangeLanes } from '../utils/annotationRange';
import {
  RangeBadge,
  RangeBandTint,
} from './AnnotationOverlay.styled';
import { BandLaneLabel, BandOuter, LaneBaseline, RangeContinues, RangeEdge } from './RangeLaneBand.styled';

// Lane geometry (px). One block per range-capable type: a label strip stacked
// directly above the pill row. Keeping the label on its own strip (rather than
// vertically centred on the pills) means it never collides with a pill that
// starts at ~0%, and keeping it INSIDE the band — instead of the old
// `right: 100%` y-axis gutter — means the narrow, `overflow:auto`-clipped
// ancestor gutter can no longer clip it. Pills still map 1:1 to [0..100%].
const LABEL_H = 16;
const PILL_H = 14;
const LANE_GAP = 8;
const TOP_PAD = 4;
const LANE_BLOCK = LABEL_H + PILL_H + LANE_GAP;

function toMs(v) {
  if (v == null) return NaN;
  return v instanceof Date ? v.getTime() : new Date(v).getTime();
}

// The band shares the trend plot's box, and the plot's D3 x-scale maps the
// domain across its FULL width: LineChart uses an x-range of [0, rect.width] for
// non-context charts (no inner margin — the y-axis tick labels live in the
// negative-x overflow to the left of x=0). So a raw domain-percent maps straight
// to a CSS percent here, with NO horizontal inset; any inset (e.g. a label
// gutter) would shift every pill right of the data below it. The lane label
// doesn't eat track width either — it floats in that same left y-axis gutter
// (see BandLaneLabel), so a range starting at ~0% no longer renders under it.
//
// `pct` -> CSS percent. The clamp is defensive only: the band feeds
// `buildRangeLanes` a RAW (unclamped) mapper so it can flag ranges that run past
// the window, and it clamps every geometry value it returns, so values are
// already in range before they reach here.
function pct(value) {
  return `${Math.max(0, Math.min(100, value))}%`;
}

// Standalone timeline strip of per-type range lanes, rendered ABOVE the trend
// plot. It mirrors the plot's visible x-domain (passed in as `domain`, kept in
// sync with zoom/pan by the chart) so each range pill aligns with the data
// below. Overlapping ranges of a type compound in opacity, and any stack carries
// a ×N badge that opens a picker — identical behaviour to the in-plot version.
function RangeLaneBand(props) {
  const { annotations, domain, onAnnotationClick, onHoverRange } = props;
  const { t } = useTranslation();
  const [rangePopover, setRangePopover] = useState(null);
  const [spotlight, setSpotlight] = useState(null);

  const startMs = toMs(domain && domain.start);
  const endMs = toMs(domain && domain.end);

  // Raw (unclamped) percent: may fall outside 0..100 when a range extends past
  // the visible window. buildRangeLanes needs that overflow to mark a pill as
  // continuing beyond an edge, so clamping must not happen before it.
  const rawXPercent = useCallback((time) => {
    const span = endMs - startMs;
    if (!Number.isFinite(span) || span === 0) return null;
    const tms = toMs(time);
    if (!Number.isFinite(tms)) return null;
    return ((tms - startMs) / span) * 100;
  }, [startMs, endMs]);

  // Clamped variant for everything that just needs a position inside the band
  // (the hover spotlight), where an off-window value would paint outside it.
  const xPercent = useCallback((time) => {
    const raw = rawXPercent(time);
    return raw === null ? null : Math.max(0, Math.min(100, raw));
  }, [rawXPercent]);

  const lanes = useMemo(() => (
    buildRangeLanes(annotations, rawXPercent, { gap: 0.4 }).map((lane, idx) => {
      const labelTop = TOP_PAD + (idx * LANE_BLOCK);
      return { ...lane, labelTop, top: labelTop + LABEL_H };
    })
  ), [annotations, rawXPercent]);

  const bandHeight = lanes.length ? TOP_PAD + (lanes.length * LANE_BLOCK) : 0;
  const bandHeightCss = `${bandHeight}px`;

  const handleSelect = useCallback((a) => {
    if (onAnnotationClick) onAnnotationClick(a);
  }, [onAnnotationClick]);

  const openRangePopover = useCallback((items, type, event) => {
    const margin = 12;
    const halfWidth = 180;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const clientX = (event && event.clientX) || vw / 2;
    const clientY = (event && event.clientY) || 0;
    const x = Math.min(Math.max(clientX, margin + halfWidth), vw - margin - halfWidth);
    setSpotlight(null);
    setRangePopover({ items, type, x, y: clientY + 10 });
  }, []);
  const closeRangePopover = useCallback(() => {
    setRangePopover(null);
    setSpotlight(null);
    if (onHoverRange) onHoverRange(null);
  }, [onHoverRange]);
  const spotlightItem = useCallback((a) => {
    const s = xPercent(a.start_time);
    const e = xPercent(a.end_time || a.start_time);
    if (s !== null && e !== null) {
      setSpotlight({
        left: Math.min(s, e),
        width: Math.abs(e - s),
        color: getAnnotationColor(a, { respectSeverity: false })
      });
    }
    if (onHoverRange) onHoverRange(a);
  }, [xPercent, onHoverRange]);
  const clearSpotlight = useCallback(() => {
    setSpotlight(null);
    if (onHoverRange) onHoverRange(null);
  }, [onHoverRange]);

  if (!lanes.length) return null;

  return (
    <BandOuter height={bandHeight}>
      {spotlight && (
        <RangeBandTint
          left={pct(spotlight.left)}
          width={pct(spotlight.width)}
          color={spotlight.color}
          height={bandHeightCss}
        />
      )}

      {lanes.map((lane) => {
        const laneMid = lane.top + (PILL_H / 2);
        return (
          <React.Fragment key={`lane-${lane.type}`}>
            <LaneBaseline top={`${laneMid}px`} />
            {lane.pills.map(pill => (
              <React.Fragment key={`pill-${pill.ref.id}`}>
                <AnnotationRangeBand
                  annotation={pill.ref}
                  left={pct(pill.left)}
                  width={pct(pill.width)}
                  color={lane.color}
                  top={`${lane.top}px`}
                  barHeight={`${PILL_H}px`}
                  plotHeight={bandHeightCss}
                  onClick={handleSelect}
                  onHoverChange={onHoverRange}
                />
                {/* A boundary tick asserts "the range starts/ends here", so it
                    is drawn only on a side that really is the range's own edge.
                    Where the range runs past the window the pill was clamped,
                    and a chevron marks that it continues beyond instead. */}
                {pill.continuesBefore ? (
                  <RangeContinues
                    top={`${lane.top}px`}
                    left={pct(pill.left)}
                    height={`${PILL_H}px`}
                    color={lane.color}
                    side="left"
                    role="img"
                    aria-label={t('annotations.range.continuesBefore', {
                      defaultValue: 'Starts before this time window'
                    })}
                  >
                    «
                  </RangeContinues>
                ) : (
                  <RangeEdge top={`${lane.top}px`} left={pct(pill.left)} height={`${PILL_H}px`} color={lane.color} />
                )}
                {pill.continuesAfter ? (
                  <RangeContinues
                    top={`${lane.top}px`}
                    left={pct(pill.left + pill.width)}
                    height={`${PILL_H}px`}
                    color={lane.color}
                    side="right"
                    role="img"
                    aria-label={t('annotations.range.continuesAfter', {
                      defaultValue: 'Continues past this time window'
                    })}
                  >
                    »
                  </RangeContinues>
                ) : (
                  <RangeEdge top={`${lane.top}px`} left={pct(pill.left + pill.width)} height={`${PILL_H}px`} color={lane.color} />
                )}
              </React.Fragment>
            ))}
            {lane.badges.map(badge => (
              <RangeBadge
                key={badge.key}
                type="button"
                top={`${laneMid}px`}
                left={pct(badge.center)}
                color={lane.color}
                onClick={(e) => { e.stopPropagation(); openRangePopover(badge.items, lane.type, e); }}
              >
                {`×${badge.items.length} ▾`}
              </RangeBadge>
            ))}
            <BandLaneLabel top={`${lane.labelTop}px`} color={lane.color}>
              {`${getAnnotationTypeLabel(t, lane.type)} ${lane.count}`}
            </BandLaneLabel>
          </React.Fragment>
        );
      })}

      {rangePopover && (
        <RangeClusterPopover
          items={rangePopover.items}
          type={rangePopover.type}
          position={{ x: rangePopover.x, y: rangePopover.y }}
          onSelect={(a) => { closeRangePopover(); handleSelect(a); }}
          onClose={closeRangePopover}
          onHoverItem={spotlightItem}
          onLeaveItem={clearSpotlight}
        />
      )}
    </BandOuter>
  );
}

RangeLaneBand.propTypes = {
  annotations: PropTypes.arrayOf(PropTypes.object),
  domain: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  onAnnotationClick: PropTypes.func,
  onHoverRange: PropTypes.func
};

RangeLaneBand.defaultProps = {
  annotations: [],
  domain: null,
  onAnnotationClick: null,
  onHoverRange: null
};

// Memoized: the band re-renders on every visible-domain change during zoom/pan
// (and its lane geometry recompute is O(n log n)); memo skips re-renders when
// the parent updates for unrelated reasons and the band's own props are unchanged.
export default React.memo(RangeLaneBand);
